import React, { FC, useCallback, useEffect } from 'react';
import { DragIcon } from '@assets/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { Box, BoxProps, Divider, Flex } from '@withjoy/joykit';
import { ChevronRightSquare, Trash } from '@withjoy/joykit/icons';
import { ResponsiveHeightProps } from '@withjoy/joykit/styled-system';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useSortableItemContext } from '@shared/components/SortableList/context/SortableItem.context';

interface Label {
  text: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  icon?: React.ReactNode;
}
interface SortableItemLayoutProps extends ResponsiveHeightProps {
  itemId: string;
  isDragActive: boolean;
  dragItemId: string | null;
  isLastItem?: boolean;
  isHover: boolean;
  icon?: React.ReactNode;
  photoUrl?: string | null;
  borderSpacing?: number;
  isDisabledDrag?: boolean;
  labels?: Label[];
  setIsHover: (isHover: boolean) => void;
  handleClick?: React.MouseEventHandler;
  handleDelete?: React.MouseEventHandler;
  hideUpdate?: boolean;
  hideDelete?: boolean;
  cardPaddingY?: BoxProps['paddingY'];
}

export const EditorSortableItemLayout: FC<SortableItemLayoutProps> = props => {
  const {
    itemId,
    isDragActive,
    dragItemId,
    isLastItem = false,
    isHover,
    height,
    icon: Icon,
    labels,
    photoUrl,
    borderSpacing,
    isDisabledDrag,
    setIsHover,
    handleClick,
    handleDelete,
    hideUpdate,
    hideDelete,
    cardPaddingY = 7,
  } = props;
  const isMobile = useIsMobileScreen();

  const hasLabels = labels && labels.length > 0;
  const { attributes, listeners, draggableNodeRef } = useSortableItemContext();

  const getActionProps = useCallback(
    () =>
      isMobile
        ? {
            onTouchStart: () => {
              setIsHover(true);
            },
            onTouchEnd: () => {
              setIsHover(false);
            },
          }
        : {
            onMouseEnter: () => setIsHover(true),
            onMouseLeave: () => setIsHover(false),
            onMouseOver: () => {
              if (!isHover) {
                setIsHover(true);
              }
            },
          },
    [isHover, isMobile, setIsHover],
  );

  // TODO: find a better way to hide the borders on hover
  useEffect(() => {
    // for removing the border top of next element when hovering on the current element
    const el: HTMLElement = document.getElementsByClassName(`virtual-wrapper-${itemId}`)[0]?.nextElementSibling?.getElementsByClassName('travelcard-container')[0] as HTMLElement;
    if (el) {
      if (isHover || (isDragActive && itemId === dragItemId)) {
        el.style.borderColor = 'transparent';
      } else if (itemId !== dragItemId) {
        el.style.borderColor = '#EBEBEB';
      }
    }
  }, [dragItemId, isDragActive, isHover, itemId]);

  return (
    <Flex
      flexDirection={'column'}
      height={height}
      overflow="hidden"
      borderRadius={4}
      alignItems="center"
      role="group"
      backgroundColor="white"
      cursor="pointer"
      width={'100%'}
      boxShadow={isDragActive && (isHover || dragItemId === itemId) ? '0px 7px 27px 0px rgba(44, 41, 37, 0.06), 0px 4px 16px 0px rgba(0, 0, 0, 0.07)' : 'none'}
    >
      <Divider
        key={`first-${itemId}`}
        className="travelcard-container"
        marginX="auto"
        width={`calc(100% - ${pxToRem(borderSpacing ?? 0)})`}
        borderColor={isHover || itemId === dragItemId ? 'transparent !important' : 'mono3'}
      />
      <Flex
        flexDirection={'column'}
        paddingY={cardPaddingY}
        paddingRight={6}
        width={'100%'}
        gap={3}
        height={hasLabels ? '100%' : 'calc(100% + 26px)'}
        _hover={{ backgroundColor: isDragActive ? 'white' : 'mono2' }}
        _pressed={{ backgroundColor: isDragActive ? 'white' : 'mono3' }}
        _active={{ backgroundColor: isDragActive ? 'white' : 'mono3' }}
        borderRadius={4}
        {...getActionProps()}
        onClick={handleClick}
      >
        {hasLabels && (
          <Flex
            position="relative"
            paddingLeft={78}
            gap={4}
            alignItems="center"
            width="100%"
            className="tags"
          >
            {labels.map((l, i) => (
              <Flex
                key={i}
                typographyVariant={'label3'}
                fontWeight={'bold'}
                borderRadius={50}
                border={'1px solid'}
                borderColor={l.borderColor}
                backgroundColor={l.bgColor}
                color={l.textColor}
                gap={3}
                paddingY={1}
                paddingX={4}
              >
                {l.icon && (
                  <Flex
                    justifyContent={'center'}
                    alignItems={'center'}
                  >
                    {l.icon}
                  </Flex>
                )}
                {l.text}
              </Flex>
            ))}
          </Flex>
        )}

        <Flex
          flexDirection={'row'}
          height={'100%'}
          width={'100%'}
          alignItems={'center'}
          borderRadius={4}
        >
          <Flex
            flexDirection="row"
            position="relative"
            alignItems="center"
            width="100%"
            height="100%"
            paddingLeft={6}
            gap={4}
          >
            {Icon && <Flex marginX={1}>{Icon}</Flex>}
            {!Icon && (
              <Box
                width={pxToRem(40)}
                height={pxToRem(40)}
                flex={`0 0 ${pxToRem(40)}`}
                border="1px solid"
                borderColor="mono3"
                borderRadius={2}
                backgroundColor="mono2"
                position="relative"
                backgroundImage={photoUrl ? `url(${photoUrl})` : ''}
                backgroundPosition="center"
                backgroundSize="cover"
                backgroundRepeat="no-repeat"
                __css={{ ['.loading > div']: { width: pxToRem(20), height: pxToRem(20) } }}
              />
            )}
            <Flex
              flexDirection="row"
              width="100%"
              alignItems="center"
              justifyContent="space-between"
              gap={4}
            >
              {props.children}
              <Flex
                position="absolute"
                right={0}
                zIndex="dropdown"
                visibility={(isHover || isMobile) && !isDragActive && !hideUpdate ? 'visible' : 'hidden'}
              >
                <ChevronRightSquare
                  size={16}
                  fill="#333"
                />
              </Flex>
            </Flex>
            {!hideDelete && (
              <Flex
                as="button"
                onClick={handleDelete}
                className="delete"
                position="absolute"
                border="2px solid"
                borderColor="mono3"
                right={pxToRem(28)}
                top="50%"
                transform="translateY(-50%)"
                borderRadius={pxToRem(20)}
                width={pxToRem(40)}
                height={pxToRem(40)}
                alignItems="center"
                justifyContent="center"
                backgroundColor="white"
                color="mono14"
                _hover={{ color: 'negative5' }}
                _active={{ color: 'negative7' }}
              >
                <Trash size="sm" />
              </Flex>
            )}
          </Flex>
          <Flex
            visibility={!isDisabledDrag ? 'visible' : 'hidden'}
            cursor={isDragActive ? 'grabbing' : 'grab'}
            height="auto"
            position="absolute"
            width={isMobile ? 10 : 6}
            alignItems="center"
            {...attributes}
            {...listeners}
            ref={draggableNodeRef}
          >
            <DragIcon />
          </Flex>
        </Flex>
      </Flex>
      {isLastItem && (
        <Divider
          key={`last-${itemId}`}
          className="travelcard-container"
          marginX="auto"
          width={`calc(100% - ${pxToRem(borderSpacing ?? 0)})`}
          borderColor={isHover ? 'transparent !important' : 'mono3'}
        />
      )}
    </Flex>
  );
};
